import { filter, subtract } from 'lodash';

import { UserRole } from '@/types';

import {
  getOrderItemPrice,
  isService,
  OLD_SERVICES_LIST_VIEW,
  productHasZeroQuantity,
} from '../../constants';
import { OrderItemWrapper, State } from '../../types';
import { isProductAvailable } from '../OrderDetailsStep/components/SelectServices/constants';

import { NextStepValidation } from './types';

export const TIMBERLINE_PRODUCT_NAME = 'Timberline Solar™';
export const DECOTECH_PRODUCT_NAME = 'Decotech®';

export const WATCHER_LIST_VALIDATION_ERRORS = {
  required: 'At least one active user is required',
  format: 'Please remove ineligible users',
};

export const calculateTotalNetPayment = (totalContractAmount: number | null, orderTotal: number) =>
  subtract(Number(totalContractAmount), orderTotal);

export const nextStepValidation = ({
  state,
  orderTotal,
  userRole,
}: NextStepValidation): string[] => {
  const errors: string[] = [];

  const totalNetPayment = calculateTotalNetPayment(state?.order.totalContractAmount, orderTotal);

  if (state.isAccessProgram && userRole === UserRole['Admin'] && totalNetPayment < 0) {
    errors.push(
      'The total contract value must be greater than the cost of materials. Please adjust the total contract value in step 1 to proceed.',
    );
  }

  if (state.order.watcherList.length === 0) {
    errors.push(WATCHER_LIST_VALIDATION_ERRORS.required);
  }

  return errors;
};

export const getNonEquipmentItems = (state: State) => {
  if (OLD_SERVICES_LIST_VIEW) {
    return filter(state.visibleOrderItems, oi => isService(oi));
  }

  if (!state.selectedServices) {
    return [];
  }

  return filter(state.visibleOrderItems, oi => {
    if (productHasZeroQuantity(oi)) {
      return false;
    }

    if (state.selectedServices === 'Design Services') {
      return isService(oi) && oi.priceBookEntry.name === state.selectedServices;
    }

    return isService(oi) && isProductAvailable(oi) && oi.priceBookEntry.name !== 'Design Services';
  });
};

export const separateNonEquipmentItems = (
  orderContext: State,
  noEquipmentItems: OrderItemWrapper[],
  proposalAdders: string[],
) =>
  noEquipmentItems.reduce(
    (
      state: {
        services: OrderItemWrapper[];
        adders: OrderItemWrapper[];
        servicesTotal: number;
        addersTotal: number;
      },
      item: OrderItemWrapper,
    ) => {
      const itemName = item.priceBookEntry.product.portalName ?? '';

      if (proposalAdders?.includes(itemName)) {
        state.addersTotal += getOrderItemPrice(orderContext, item);

        return { ...state, adders: [...state.adders, item] };
      }

      state.servicesTotal += getOrderItemPrice(orderContext, item);

      return { ...state, services: [...state.services, item] };
    },
    {
      services: [],
      adders: [],
      servicesTotal: 0,
      addersTotal: 0,
    },
  );
